import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Car, Calendar, Clock, User, Mail, Phone, MapPin, 
  CheckCircle, XCircle, Loader2, RefreshCw, ArrowLeft,
  ClipboardList, TrendingUp, AlertCircle, Home, Building2
} from "lucide-react";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Header = () => (
  <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-zinc-800">
    <div className="container-custom">
      <div className="flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
            <Car className="w-6 h-6 text-black" />
          </div>
          <span className="text-xl font-medium tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Esthétique Auto TR
          </span>
        </Link>
        <Link to="/">
          <Button variant="ghost" className="text-zinc-400 hover:text-white" data-testid="back-home-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au site
          </Button>
        </Link>
      </div>
    </div>
  </header>
);

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Card className="bg-zinc-900 border-zinc-800">
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{label}</p>
          <p className="text-3xl font-medium text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            {value}
          </p>
        </div>
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { label: "En attente", class: "bg-yellow-500/20 text-yellow-500 border border-yellow-500/30" },
    confirmed: { label: "Confirmé", class: "bg-blue-500/20 text-blue-500 border border-blue-500/30" },
    completed: { label: "Terminé", class: "bg-green-500/20 text-green-500 border border-green-500/30" },
    cancelled: { label: "Annulé", class: "bg-red-500/20 text-red-500 border border-red-500/30" }
  };

  const config = statusConfig[status] || statusConfig.pending;

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.class}`} data-testid={`status-badge-${status}`}>
      {config.label}
    </span>
  );
};

const AppointmentCard = ({ appointment, onStatusChange, onDelete }) => {
  const [updating, setUpdating] = useState(false);

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      await onStatusChange(appointment.id, newStatus);
    } finally {
      setUpdating(false);
    }
  };

  const formattedDate = appointment.date 
    ? format(parseISO(appointment.date), "EEEE d MMMM yyyy", { locale: fr })
    : "";

  return (
    <Card className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-all duration-300" data-testid={`appointment-card-${appointment.id}`}>
      <CardContent className="p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          
          {/* Main Info */}
          <div className="flex-1 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-medium text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  {appointment.client_name}
                </h3>
                <p className="text-sm text-zinc-400">{appointment.service_name}</p>
              </div>
              <StatusBadge status={appointment.status} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
              <div className="flex items-center gap-2 text-zinc-400">
                <Calendar className="w-4 h-4 text-white" />
                <span className="capitalize">{formattedDate}</span>
              </div>
              <div className="flex items-center gap-2 text-zinc-400">
                <Clock className="w-4 h-4 text-white" />
                <span>{appointment.time_slot}</span>
              </div>
              <div className="flex items-center gap-2 text-zinc-400">
                <Phone className="w-4 h-4 text-white" />
                <span>{appointment.client_phone}</span>
              </div>
              <div className="flex items-center gap-2 text-zinc-400">
                <Mail className="w-4 h-4 text-white" />
                <span className="truncate">{appointment.client_email}</span>
              </div>
              <div className="flex items-center gap-2 text-zinc-400 sm:col-span-2">
                {appointment.location_type === 'garage' ? (
                  <Building2 className="w-4 h-4 text-white" />
                ) : (
                  <Home className="w-4 h-4 text-white" />
                )}
                <span>
                  {appointment.location_type === 'garage' ? 'En garage' : 'À domicile'}
                  {appointment.address && ` - ${appointment.address}`}
                </span>
              </div>
            </div>

            {(appointment.vehicle_brand || appointment.vehicle_model) && (
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <Car className="w-4 h-4 text-white" />
                <span>{appointment.vehicle_brand} {appointment.vehicle_model}</span>
              </div>
            )}

            {appointment.selected_extras && appointment.selected_extras.length > 0 && (
              <div className="text-xs text-zinc-500">
                <strong className="text-zinc-400">Extras:</strong> {appointment.selected_extras.join(', ')}
              </div>
            )}

            {appointment.notes && (
              <p className="text-xs text-zinc-500 bg-black p-3 rounded-lg border border-zinc-800">
                <strong>Notes:</strong> {appointment.notes}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-row lg:flex-col gap-2 lg:min-w-[160px]">
            <Select 
              value={appointment.status} 
              onValueChange={handleStatusChange}
              disabled={updating}
            >
              <SelectTrigger 
                className="bg-black border-zinc-800 text-white text-sm"
                data-testid={`status-select-${appointment.id}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="pending" className="text-white hover:bg-zinc-800">En attente</SelectItem>
                <SelectItem value="confirmed" className="text-white hover:bg-zinc-800">Confirmé</SelectItem>
                <SelectItem value="completed" className="text-white hover:bg-zinc-800">Terminé</SelectItem>
                <SelectItem value="cancelled" className="text-white hover:bg-zinc-800">Annulé</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(appointment.id)}
              className="border-red-500/30 text-red-500 hover:bg-red-500/10 hover:border-red-500/50"
              data-testid={`delete-btn-${appointment.id}`}
            >
              <XCircle className="w-4 h-4 mr-1" />
              Supprimer
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminPage = () => {
  const [appointments, setAppointments] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, completed: 0, cancelled: 0 });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appointmentsRes, statsRes] = await Promise.all([
        axios.get(`${API}/appointments`),
        axios.get(`${API}/stats`)
      ]);
      setAppointments(appointmentsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      await axios.patch(`${API}/appointments/${appointmentId}`, { status: newStatus });
      toast.success("Statut mis à jour");
      fetchData();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDelete = async (appointmentId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce rendez-vous ?")) return;
    
    try {
      await axios.delete(`${API}/appointments/${appointmentId}`);
      toast.success("Rendez-vous supprimé");
      fetchData();
    } catch (error) {
      console.error("Error deleting appointment:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const filteredAppointments = filterStatus === "all" 
    ? appointments 
    : appointments.filter(apt => apt.status === filterStatus);

  return (
    <div className="min-h-screen bg-black" data-testid="admin-page">
      <Header />
      
      <main className="pt-28 pb-16">
        <div className="container-custom">
          
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <span className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-2 inline-block">Administration</span>
              <h1 className="text-2xl sm:text-3xl tracking-tight font-medium text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Gestion des Rendez-vous
              </h1>
            </div>
            <Button 
              onClick={fetchData} 
              disabled={loading}
              className="bg-white text-black hover:bg-zinc-200"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <StatCard 
              icon={ClipboardList} 
              label="Total" 
              value={stats.total} 
              color="bg-white/10 text-white"
            />
            <StatCard 
              icon={AlertCircle} 
              label="En attente" 
              value={stats.pending} 
              color="bg-yellow-500/20 text-yellow-500"
            />
            <StatCard 
              icon={CheckCircle} 
              label="Confirmés" 
              value={stats.confirmed} 
              color="bg-blue-500/20 text-blue-500"
            />
            <StatCard 
              icon={TrendingUp} 
              label="Terminés" 
              value={stats.completed} 
              color="bg-green-500/20 text-green-500"
            />
            <StatCard 
              icon={XCircle} 
              label="Annulés" 
              value={stats.cancelled} 
              color="bg-red-500/20 text-red-500"
            />
          </div>

          {/* Filter */}
          <div className="flex items-center gap-4 mb-6">
            <span className="text-sm text-zinc-500">Filtrer par statut:</span>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger 
                className="w-[180px] bg-zinc-900 border-zinc-800 text-white"
                data-testid="filter-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all" className="text-white hover:bg-zinc-800">Tous</SelectItem>
                <SelectItem value="pending" className="text-white hover:bg-zinc-800">En attente</SelectItem>
                <SelectItem value="confirmed" className="text-white hover:bg-zinc-800">Confirmés</SelectItem>
                <SelectItem value="completed" className="text-white hover:bg-zinc-800">Terminés</SelectItem>
                <SelectItem value="cancelled" className="text-white hover:bg-zinc-800">Annulés</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Appointments List */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-white animate-spin" />
            </div>
          ) : filteredAppointments.length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="py-16 text-center">
                <Calendar className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-lg text-white mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  Aucun rendez-vous
                </h3>
                <p className="text-sm text-zinc-500">
                  {filterStatus === "all" 
                    ? "Aucun rendez-vous n'a encore été pris."
                    : "Aucun rendez-vous avec ce statut."}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredAppointments.map(appointment => (
                <AppointmentCard
                  key={appointment.id}
                  appointment={appointment}
                  onStatusChange={handleStatusChange}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminPage;
